// src/components/MatchReconciliationPanel.tsx
// G06 / R05 — Three-way match reconciliation UI (Sprint 2)
// Surfaces PO ↔ GRN ↔ Invoice match results with approve / dispute actions.

import { useState } from 'react'
import { CheckCircle, AlertTriangle, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react'
import { MOCK_MATCH_RESULTS, MATCH_STATUS_META } from '../utils/mockDataGRN'

const fmtAUD = (n: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2 }).format(n)

const ROW_LEFT_BORDER: Record<string, string> = {
  FULL_MATCH:        'border-l-green-400',
  PRICE_VARIANCE:    'border-l-amber-400',
  QUANTITY_VARIANCE: 'border-l-red-400',
  DISPUTED:          'border-l-orange-400',
  APPROVED:          'border-l-sky-400',
  MISSING_GRN:       'border-l-gray-200',
  MISSING_PO:        'border-l-gray-200',
}

type RowApproval = 'APPROVED' | 'DISPUTED'

export function MatchReconciliationPanel() {
  const matches = MOCK_MATCH_RESULTS
  const [approvals, setApprovals]           = useState<Record<string, RowApproval>>({})
  const [disputeNotes, setDisputeNotes]     = useState<Record<string, string>>({})
  const [showDisputeRow, setShowDisputeRow] = useState<Record<string, boolean>>({})
  const [expanded, setExpanded]             = useState<Record<string, boolean>>({})

  const approve = (id: string) => setApprovals(p => ({ ...p, [id]: 'APPROVED' }))
  const dispute = (id: string) => setApprovals(p => ({ ...p, [id]: 'DISPUTED' }))

  const pendingFullMatches = matches.filter(
    m => m.matchStatus === 'FULL_MATCH' && !approvals[m.id]
  )

  const bulkApprove = () =>
    setApprovals(p => ({
      ...p,
      ...Object.fromEntries(pendingFullMatches.map(m => [m.id, 'APPROVED' as const])),
    }))

  const approvedCount = Object.values(approvals).filter(v => v === 'APPROVED').length
  const disputedCount = Object.values(approvals).filter(v => v === 'DISPUTED').length
  const varianceCount = matches.filter(m =>
    ['PRICE_VARIANCE','QUANTITY_VARIANCE','DISPUTED'].includes(m.matchStatus)
  ).length

  return (
    <div className="space-y-4">
      {/* Summary strip */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total in match queue', value: matches.length,                                                  color: 'text-gray-900' },
          { label: 'Full match',           value: matches.filter(m => m.matchStatus === 'FULL_MATCH').length,      color: 'text-green-700' },
          { label: 'Variances',            value: varianceCount,                                                   color: varianceCount > 0 ? 'text-amber-600' : 'text-gray-400' },
          { label: 'Approved this session',value: approvedCount,                                                   color: approvedCount > 0 ? 'text-sky-600' : 'text-gray-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Bulk approve strip */}
      {pendingFullMatches.length > 0 && (
        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <div className="text-sm text-green-800">
            <span className="font-semibold">{pendingFullMatches.length} invoice{pendingFullMatches.length !== 1 ? 's' : ''}</span>{' '}
            fully match their PO and GRN — no variances. Bulk approve to clear for payment.
          </div>
          <button onClick={bulkApprove}
            className="text-sm bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-1.5 flex-shrink-0">
            <CheckCircle className="w-4 h-4" />Bulk approve {pendingFullMatches.length}
          </button>
        </div>
      )}

      {/* Match table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 text-[10px] font-semibold text-gray-400 uppercase tracking-wide px-4 py-2 border-b bg-gray-50">
          <div className="col-span-3">Description / invoice</div>
          <div className="col-span-1 text-right">PO qty</div>
          <div className="col-span-1 text-right">GRN qty</div>
          <div className="col-span-1 text-right">Inv qty</div>
          <div className="col-span-1 text-right">Qty Δ</div>
          <div className="col-span-1 text-right">Price %Δ</div>
          <div className="col-span-1 text-right">Value Δ</div>
          <div className="col-span-1 text-center">Status</div>
          <div className="col-span-2 text-right">Action</div>
        </div>

        <div className="divide-y divide-gray-50">
          {matches.map(m => {
            const mm          = MATCH_STATUS_META[m.matchStatus]
            const rowApproval = approvals[m.id]
            const isOpen      = !!expanded[m.id]

            const leftBorder = rowApproval === 'APPROVED' ? 'border-l-sky-400'
              : rowApproval === 'DISPUTED' ? 'border-l-orange-400'
              : ROW_LEFT_BORDER[m.matchStatus] ?? 'border-l-gray-200'

            const rowBg = rowApproval === 'APPROVED' ? 'bg-sky-50'
              : rowApproval === 'DISPUTED' ? 'bg-orange-50'
              : m.matchStatus === 'FULL_MATCH'        ? 'bg-green-50/60'
              : m.matchStatus === 'PRICE_VARIANCE'    ? 'bg-amber-50/60'
              : m.matchStatus === 'QUANTITY_VARIANCE' ? 'bg-red-50/60'
              : 'bg-white'

            return (
              <div key={m.id} className={`border-l-2 ${leftBorder} ${rowBg}`}>
                <div className="grid grid-cols-12 items-center px-4 py-2.5 text-xs gap-0">
                  <div className="col-span-3 pr-3">
                    <div className="font-medium text-gray-900 truncate">{m.invDescription ?? m.poDescription ?? '—'}</div>
                    <div className="text-[10px] text-gray-400 font-mono mt-0.5">{m.invoiceNumber}</div>
                  </div>
                  <div className="col-span-1 text-right tabular-nums text-gray-500">{m.poOrderedQty != null ? m.poOrderedQty : '—'}</div>
                  <div className="col-span-1 text-right tabular-nums text-gray-500">{m.grnReceivedQty != null ? m.grnReceivedQty : <span className="text-gray-300">—</span>}</div>
                  <div className={`col-span-1 text-right tabular-nums font-semibold ${m.qtyVariance && m.qtyVariance > 0 ? 'text-red-600' : m.qtyVariance && m.qtyVariance < 0 ? 'text-green-600' : 'text-gray-700'}`}>{m.invQty}</div>
                  <div className={`col-span-1 text-right tabular-nums ${m.qtyVariance ? m.qtyVariance > 0 ? 'text-red-600 font-bold' : m.qtyVariance < 0 ? 'text-green-600 font-semibold' : 'text-gray-400' : 'text-gray-400'}`}>
                    {m.qtyVariance != null ? m.qtyVariance > 0 ? `+${m.qtyVariance}` : m.qtyVariance === 0 ? '—' : m.qtyVariance : '—'}
                  </div>
                  <div className={`col-span-1 text-right tabular-nums ${m.priceVariancePct ? Math.abs(m.priceVariancePct) > 5 ? 'text-red-600 font-bold' : 'text-amber-600 font-semibold' : 'text-gray-400'}`}>
                    {m.priceVariancePct != null && m.priceVariancePct !== 0 ? `${m.priceVariancePct > 0 ? '+' : ''}${m.priceVariancePct.toFixed(1)}%` : '—'}
                  </div>
                  <div className={`col-span-1 text-right tabular-nums ${m.valueVariance ? m.valueVariance > 0 ? 'text-red-600 font-bold' : m.valueVariance < 0 ? 'text-green-600 font-semibold' : 'text-gray-400' : 'text-gray-400'}`}>
                    {m.valueVariance != null && m.valueVariance !== 0 ? m.valueVariance > 0 ? `+${fmtAUD(m.valueVariance)}` : `-${fmtAUD(Math.abs(m.valueVariance))}` : '—'}
                  </div>
                  <div className="col-span-1 text-center">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${mm.bg} ${mm.text}`}>{mm.icon}</span>
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-1">
                    {rowApproval === 'APPROVED' && <span className="text-[10px] text-sky-600 font-semibold flex items-center gap-0.5"><CheckCircle className="w-3 h-3" />Approved</span>}
                    {rowApproval === 'DISPUTED' && <span className="text-[10px] text-orange-600 font-semibold flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" />Disputed</span>}
                    {!rowApproval && !['MISSING_GRN','MISSING_PO'].includes(m.matchStatus) && (
                      <>
                        <button onClick={() => approve(m.id)} className="text-[10px] px-1.5 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 font-medium">Approve</button>
                        {['PRICE_VARIANCE','QUANTITY_VARIANCE'].includes(m.matchStatus) && (
                          <button onClick={() => setShowDisputeRow(p => ({ ...p, [m.id]: !p[m.id] }))} className="text-[10px] px-1.5 py-1 rounded bg-orange-100 text-orange-700 hover:bg-orange-200 font-medium">Dispute</button>
                        )}
                      </>
                    )}
                    <button onClick={() => setExpanded(p => ({ ...p, [m.id]: !isOpen }))} className="text-gray-300 hover:text-gray-500 ml-0.5">
                      {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>

                {showDisputeRow[m.id] && !rowApproval && (
                  <div className="px-4 pb-3 flex items-center gap-2">
                    <MessageSquare className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                    <input className="input text-xs flex-1" placeholder="Dispute reason — credit note requested, qty shortfall, incorrect pricing…"
                      value={disputeNotes[m.id] ?? ''} onChange={e => setDisputeNotes(p => ({ ...p, [m.id]: e.target.value }))} />
                    <button disabled={!disputeNotes[m.id]?.trim()}
                      onClick={() => { dispute(m.id); setShowDisputeRow(p => ({ ...p, [m.id]: false })) }}
                      className="text-xs px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium disabled:opacity-50">
                      Confirm
                    </button>
                  </div>
                )}

                {isOpen && (m.varianceDescription || m.poRef) && (
                  <div className="px-4 pb-3 space-y-2">
                    <div className="flex gap-3 text-[10px] text-gray-400 font-mono">
                      {m.poRef && <span>PO: {m.poRef}</span>}
                      {m.grnNumber && <span>GRN: {m.grnNumber}</span>}
                    </div>
                    {m.varianceDescription && (
                      <div className={`text-xs rounded-lg px-3 py-2 ${m.matchStatus === 'QUANTITY_VARIANCE' ? 'bg-red-50 border border-red-200 text-red-800' : 'bg-amber-50 border border-amber-200 text-amber-800'}`}>
                        <div className="font-semibold mb-0.5">Variance detail</div>
                        {m.varianceDescription}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div className="flex items-center gap-5 flex-wrap text-[10px] text-gray-500 pt-1">
        {[
          { color: 'bg-green-200', label: 'Full match — bulk-approvable' },
          { color: 'bg-amber-200', label: 'Price variance — PM review' },
          { color: 'bg-red-200',   label: 'Quantity variance — hold payment' },
          { color: 'bg-sky-200',   label: 'Approved — cleared for payment' },
        ].map(({ color, label }) => (
          <span key={label} className="flex items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-sm ${color}`} />
            {label}
          </span>
        ))}
      </div>

      {disputedCount > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-xs text-orange-800">
          <span className="font-semibold">{disputedCount} invoice{disputedCount !== 1 ? 's' : ''} disputed</span>{' '}
          — payment is held pending resolution with the supplier.
        </div>
      )}
    </div>
  )
}
